import { formatDateToISO, pascalCase } from "utils";

import { DEFAULT_SELLER_SORT, JOPL, JODL } from "./constants";

export const isFileSizeWithinLimit = (file, maxSizeMb) =>
  Boolean(file) && file.size <= maxSizeMb * 1024 * 1024;

export const isFileTypeAccepted = (file, acceptedTypes) => {
  if (!file) return false;

  const extension = `.${file.name.split(".").pop()}`.toLowerCase();
  return acceptedTypes
    .split(",")
    .map((type) => type.trim().toLowerCase())
    .includes(extension);
};

export const buildUploadFormData = (file, actionType) => {
  const formData = new FormData();
  formData.append("DMSUpload", "false");
  formData.append("actionType", actionType);
  formData.append("file", file);
  return formData;
};

export const isEndpointConfigured = (url) => Boolean(url);

export const extractUploadRequestId = (responseData) =>
  responseData?.fileUploadId ||
  responseData?.requestId ||
  responseData?.data?.requestId ||
  responseData?.data?.requestNumber ||
  responseData?.requestNumber ||
  null;

export const buildUploadSuccessMessage = (responseData = {}) => {
  const requestId = extractUploadRequestId(responseData);

  if (requestId) {
    return `Upload request: REQ-${requestId} is successfully done.`;
  }

  return responseData?.remarks || "File uploaded successfully.";
};

const getNumber = (...values) => {
  const value = values.find((item) => item !== undefined && item !== null);
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getSellerVmId = (seller = {}) => seller.vmId ?? seller.VmId;

const getSellerVmIds = (sellers = []) =>
  sellers.map((seller) => getSellerVmId(seller)).filter(Boolean);

const getVmIdsBySellerNames = (sellerList = [], sellerNames = []) => {
  if (!sellerList.length || !sellerNames.length) return [];

  const vmIdMap = sellerList.reduce((map, seller) => {
    const companyName = seller.companyName;
    const vmId = getSellerVmId(seller);

    if (!vmId) return map;

    if (!map.has(companyName)) {
      map.set(companyName, []);
    }

    map.get(companyName).push(vmId);

    return map;
  }, new Map());

  return sellerNames.flatMap((name) => vmIdMap.get(name) || []);
};

const getVmIdsByStates = (sellerList = [], states = []) => {
  if (!sellerList.length || !states.length) return [];

  return sellerList
    .filter(({ state }) => states.includes(state))
    .map((seller) => getSellerVmId(seller))
    .filter(Boolean);
};

const buildSellerAnalyticsPayload = (
  activeFilters = {},
  activeSort = DEFAULT_SELLER_SORT,
  sellerPage = 0,
  sellerRowsPerPage = 10,
) => ({
  page: sellerPage + 1,
  pageSize: sellerRowsPerPage,
  ...activeFilters,
  sortBy: activeSort.sortBy,
  sortDir: activeSort.sortDir,
});

const buildSellerFilters = (filterPayload = {}, sellerList = []) => {
  const { dateFrom, dateTo, sellerNames, sellerStates, soNumber, slo } =
    filterPayload;
  const sellerVmIds = getVmIdsBySellerNames(sellerList, sellerNames);
  const stateVmIds = getVmIdsByStates(sellerList, sellerStates);

  return {
    soDateFrom: formatDateToISO(dateFrom),
    soDateTo: formatDateToISO(dateTo),
    soNumber,
    orderNumber: slo,
    sourceSeller: [...new Set([...sellerVmIds, ...stateVmIds])],
  };
};

const getNextSellerSort = (currentSort, sortBy) => ({
  sortBy,
  sortDir:
    currentSort.sortBy === sortBy && currentSort.sortDir === "ASC"
      ? "DESC"
      : "ASC",
});

const buildOrdersSearchPayload = ({
  page,
  rowsPerPage,
  filters,
  selectedVmIds,
  tableFilters,
}) => ({
  page,
  pageSize: rowsPerPage,
  soNumber: filters?.soNumber || null,
  orderNumber: filters?.orderNumber || null,
  soDateFrom: filters?.soDateFrom || null,
  soDateTo: filters?.soDateTo || null,
  ...(selectedVmIds?.length ? { sourceSeller: selectedVmIds } : {}),
  ...(tableFilters ? { filterType: tableFilters } : {}),
});

const normalizeSellerAnalyticsData = (data = {}) => ({
  pagination: data?.pagination || {},
  sellers: Array.isArray(data?.sellers)
    ? data.sellers.map((seller) => ({
        companyName: seller.companyName || "-",
        fgReadiness: getNumber(seller.fgReadiness),
        openOrders: getNumber(seller.openOrders),
        tonnageDue: getNumber(seller.tonnageDue),
        fgReadyQty: getNumber(seller.fgReadyQty),
        orderedQty: getNumber(seller.orderedQty),
        invoicedQty: getNumber(seller.invoicedQty),
        vmId: seller.vmId || "-",
      }))
    : [],
});

const getMetricTone = (value, thresholds = {}) => {
  const number = getSafeNumber(value);

  const ranges = [
    ["green", thresholds.greenMin, thresholds.greenMax],
    ["amber", thresholds.amberMin, thresholds.amberMax],
    ["red", thresholds.redMin, thresholds.redMax],
  ];

  return (
    ranges.find(([, min, max]) => number >= min && number <= max)?.[0] || "red"
  );
};

const getSafeNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatPercent = (value) => `${Math.round(getSafeNumber(value))}%`;
const formatTonnage = (value) => `${getSafeNumber(value).toFixed(1)} MT`;

const getStatusTone = (status, thresholds = {}) => {
  if (!status) return "red";

  const normalizedStatus = String(status).trim().toUpperCase();

  if ((thresholds.greenStatuses || []).includes(normalizedStatus)) {
    return "green";
  }

  if ((thresholds.amberStatuses || []).includes(normalizedStatus)) {
    return "amber";
  }

  return "red";
};

const getToneColor = (theme, tone) => {
  if (tone === "green") return theme.palette.green.dark;
  if (tone === "amber") return theme.palette.warning.dark;
  return theme.palette.error.light;
};

const getAgeingBgColor = (theme, tone) => {
  if (tone === "green") return theme.palette.green.light;
  if (tone === "amber") return theme.palette.warning.light;
  return theme.palette.error.errorLightBgColor;
};

export const parseMtValue = (value) => {
  if (!value || typeof value !== "string") {
    return 0;
  }
  const parsed = Number.parseFloat(value.replace(" MT", ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const getFgToneColor = (tone, theme) => {
  if (tone === "success") {
    return theme.palette.green.dark;
  }

  if (tone === "warning") {
    return theme.palette.warning.dark;
  }

  if (tone === "danger") {
    return theme.palette.error.dark;
  }

  return theme.palette.text.secondary;
};

const parseOrderConfirmedAt = (value) => {
  if (!value) return null;

  if (Array.isArray(value)) {
    const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] =
      value;
    return new Date(
      year,
      month - 1,
      day,
      hour,
      minute,
      second,
      Math.floor(nano / 1000000),
    );
  }
  return new Date(value);
};

export const getFgAwaitedTone = (lineItem) => {
  const confirmationDate = parseOrderConfirmedAt(lineItem.orderConfirmedAt);

  if (!confirmationDate || Number.isNaN(confirmationDate.getTime())) {
    return "neutral";
  }

  const hoursSinceConfirmation =
    (Date.now() - confirmationDate.getTime()) / (1000 * 60 * 60);

  if (hoursSinceConfirmation <= 4) {
    return "success";
  }

  if (hoursSinceConfirmation <= 24) {
    return "warning";
  }

  return "danger";
};

export const getPayerCodeChipStyles = (payerCode, theme) => {
  if (payerCode === JOPL) {
    return {
      bgColor: theme.palette.info.navyBlueBackground,
      textColor: theme.palette.info.blueColor,
    };
  }

  if (payerCode === JODL) {
    return {
      bgColor: theme.palette.avatar.blue,
      textColor: theme.palette.info.skyBlue,
    };
  }

  return {
    bgColor: theme.palette.grey[100],
    textColor: theme.palette.text.secondary,
  };
};

export {
  buildOrdersSearchPayload,
  buildSellerAnalyticsPayload,
  buildSellerFilters,
  formatPercent,
  formatTonnage,
  getAgeingBgColor,
  getMetricTone,
  getSafeNumber,
  getSellerVmId,
  getSellerVmIds,
  getNextSellerSort,
  getStatusTone,
  getToneColor,
  normalizeSellerAnalyticsData,
};

export const MARK_CLOSED_REASONS = [
  "Fully delivered",
  "Cancelled by buyer",
  "Duplicate SO",
  "Replaced or remapped",
  "Order Shortclosed",
  "Others",
];

export const OTHERS_VALUE = "Others";

export const getInvoiceStatusInfo = (invoicedQty, orderQty, theme) => {
  const invoiced = Number(invoicedQty) || 0;
  const ordered = Number(orderQty) || 0;

  const commonColors = {
    bgColor: theme.palette.info.navyBlueBackground,
    textColor: theme.palette.info.blueColor,
  };

  let label = "Partially Invoiced";
  if (invoiced === 0) {
    label = "Open";
  } else if (invoiced >= ordered) {
    label = "Fully Invoiced";
  }

  return { label, ...commonColors };
};

export const formatMt = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "0 MT";
  }
  return `${value} MT`;
};

const validateValue = (value) => {
  return value ? `${value},` : "";
};

const getFormattedAddress = (address = {}) => {
  if (!address || Object.keys(address).length === 0) {
    return "-";
  }
  const { addressLine1, addressLine2, city, state, pincode } = address || {};
  return `
  ${validateValue(addressLine1)}
  ${validateValue(addressLine2)}
  ${validateValue(city)}
  ${validateValue(state)}
  ${validateValue(pincode)}`;
};

const getOrderFgReadyTone = (fgReadyQty, totalQty) => {
  const ready = Number(fgReadyQty || 0);
  const total = Number(totalQty || 0);
  return ready === total ? "success" : ready > 0 ? "warning" : "danger";
};

export const normalizeSoBoardData = (response = {}, selectedTab) => ({
  pagination: response.pagination || {},
  orders: (Array.isArray(response.data) ? response.data : [])
    .filter(Boolean)
    .map((order) => mapOrder(order, selectedTab)),
});

const mapLineItem = (item, order) => ({
  id: item?.materialId || "-",
  sku: item?.sku || "-",
  description: item?.description || "-",
  orderQty: formatMt(item?.orderQty),
  fgReady: formatMt(item?.fgReadyQty),
  fgAwaited: formatMt(item?.fgAwaitedQty),
  netValue: item?.rateExTax || 0,
  sellerOrderRef: item?.olRef || "-",
  orderConfirmedAt: order?.orderConfirmedAt || null,
  rawSoListingId: item?.rawSoListingId,
  batchUpdates: item?.batchUpdates || [],
});

export const mapOrder = (order, selectedTab) => ({
  id: order?.id || "-",
  orderId: order?.rawSoListingId || "-",
  payerCode: order?.payerCode || "-",
  sellerName: pascalCase(order?.sourceSeller) || "-",
  vendorLocation: getFormattedAddress(order?.sourceSellerAddress),
  soNumber: order?.sellerOrderRef || "-",
  sellerOrder: order?.sellerOrderNumber || "",
  soType: order?.soType || "-",
  buyerName: pascalCase(order?.buyerName) || "-",
  shipTo: order?.shipTo || "-",
  totalQty: formatMt(order?.orderedQty),
  invoicedQty: formatMt(order?.invoicedQty),
  fgReady: `${formatMt(order?.fgReadyQty).replace(" MT", "")} / ${formatMt(
    order?.orderedQty,
  )}`,
  fgReadyTone: getOrderFgReadyTone(order?.fgReadyQty, order?.orderedQty),
  fgAwaited: formatMt(order?.fgAwaited),
  netLandedValue: order?.netLandedVal || 0,
  paymentMode: order?.paymentMode || "-",
  closureType: order?.closureType || "-",
  rawSoListingStatus: order?.rawSoListingStatus || "-",
  tab: selectedTab || "",
  buyerGstin: order?.buyerGstin || "-",
  sellerGstin: order?.sellerGstin || "-",
  status: order?.status || "-",
  orderStatus: order?.orderStatus || null,
  lineItems: (order?.lineitems || [])
    .filter(Boolean)
    .map((lineItem) => mapLineItem(lineItem, order)),
});

export const normalizeTabCounts = (response = {}) => ({
  unmappedso: response?.tabCounts?.unmapped || 0,
  abandonedso: response?.tabCounts?.abondoned || 0,
  terminalorderso: response?.tabCounts?.terminalOrders || 0,
  historicalso: response?.tabCounts?.historical || 0,
});
